import {
  Autocomplete,
  Box,
  CircularProgress,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { Observable, finalize } from 'rxjs';
import { ISelectOption } from 'src/Shared/Interfaces';
import { useStyles } from './AsyncAutocomplete.styles';
import useDebounce from 'src/Shared/Hooks/UseDebounce.hook';
import { CommonService } from 'src/Shared/Services';

type Props = {
  label?: string;
  apiObservable?(search?: string): Observable<ISelectOption[]>;
  onValueChange?: (value: any) => void;
  sx?: SxProps<Theme>;
  value?: any;
  readOnly?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  /** Shown beneath the field; carries the validation message when one applies. */
  helperText?: React.ReactNode;
  /** Renders the required asterisk on the label. */
  required?: boolean;
  hideValueInOption?: boolean;
  // Permanent sticky options
  stickyOptions?: ISelectOption[];
};

const AsyncAutocomplete: React.FC<Props> = (
  {
    label,
    apiObservable,
    onValueChange,
    sx,
    value,
    readOnly,
    multiple,
    disabled,
    error,
    helperText,
    required,
    hideValueInOption,
    stickyOptions = [],
  },
  props
) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = React.useState<ISelectOption[]>([]);
  const [inputValue, setinputValue] = React.useState<string>('');

  const onInputChange = (val: string) => {
    setinputValue(val);
  };

  useDebounce(
    () => {
      getOptions();
    },
    500,
    [inputValue]
  );

  const onOpen = () => {
    if (options.length === 0) {
      getOptions();
    }
  };

  function getOptions() {
    setLoading(true);
    if (apiObservable && apiObservable(inputValue)) {
      apiObservable(inputValue)
        .pipe(
          finalize(() => {
            setLoading(false);
          })
        )
        .subscribe((apiOptions) => {
          setOptions(apiOptions);
        });
    }
  }

  // Create enhanced options with permanent sticky options
  const getEnhancedOptions = (): ISelectOption[] => {
    let enhancedOptions = [...options];

    // Add permanent sticky options at the beginning (always visible)
    if (stickyOptions.length > 0) {
      const permanentStickyOptions = stickyOptions.map((option) => ({
        ...option,
        isPermanentSticky: true,
      }));

      enhancedOptions = [...enhancedOptions, ...permanentStickyOptions];
    }

    return enhancedOptions;
  };

  const enhancedOptions = getEnhancedOptions();

  return (
    <>
      <Autocomplete
        disabled={disabled}
        multiple={multiple}
        sx={sx}
        value={value}
        fullWidth
        onOpen={onOpen}
        options={enhancedOptions}
        getOptionLabel={(option) => option?.label || ''}
        loading={loading}
        onInputChange={(e, value) => onInputChange(value)}
        getOptionKey={(option) => option.value}
        onChange={(_, selectedValue) => {
          if (onValueChange) {
            // Check if it's a permanent sticky option
            if (selectedValue && (selectedValue as any).isPermanentSticky) {
              // Create a clean option without sticky properties
              const cleanOption: ISelectOption = {
                value: selectedValue.value,
                label: selectedValue.label,
              };
              onValueChange(cleanOption);
            } else {
              onValueChange(selectedValue);
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            value={inputValue || ''}
            label={label}
            error={error}
            required={required}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          // Special rendering for permanent sticky options
          if ((option as any).isPermanentSticky) {
            return (
              <Box
                component="li"
                {...props}
                sx={{
                  backgroundColor: '#e3f2fd',
                  borderBottom: '1px solid #bbdefb',
                  '&:hover': {
                    backgroundColor: '#bbdefb',
                  },
                  fontWeight: '500',
                }}
              >
                <Box className={classes.optionBox}>
                  <Box>{option.label}</Box>
                  {!hideValueInOption && <Box>{option.value}</Box>}
                </Box>
              </Box>
            );
          }

          // Regular option rendering
          return (
            <Box component="li" {...props}>
              <Box className={classes.optionBox}>
                <Box> {option.label}</Box>
                {!hideValueInOption && <Box> {option.value}</Box>}
              </Box>
            </Box>
          );
        }}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        readOnly={readOnly}
        filterOptions={(options) => options}
      />
    </>
  );
};

AsyncAutocomplete.defaultProps = {
  apiObservable(search) {
    return CommonService.getUsers(search, 20);
  },
  value: '',
  hideValueInOption: false,
  stickyOptions: [],
};

export default AsyncAutocomplete;
